/**
 * PDF Generation Utility
 *
 * Uses server-side Puppeteer rendering for high-quality PDF output.
 * Copies the proven pattern from ViewQuote.jsx.
 */

/**
 * Detect Safari browser
 */
const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
};

/**
 * Compress images in cloned DOM to reduce PDF size.
 * Converts images to compressed JPEGs with max dimensions.
 *
 * @param {HTMLElement} clone - Cloned DOM element containing images
 * @param {number} maxWidth - Maximum width for images (default 800px)
 * @param {number} quality - JPEG quality 0-1 (default 0.7)
 */
async function compressImages(clone, maxWidth = 800, quality = 0.7) {
  const images = clone.querySelectorAll('img');

  for (const img of images) {
    // Skip if no src, if it's an SVG, or if it's already a compressed data URL
    if (!img.src || img.src.startsWith('data:image/svg') || img.src.startsWith('data:image/jpeg')) continue;

    try {
      // Create canvas for compression
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Create temp image to get dimensions
      const tempImg = new Image();

      // Try loading with CORS first, fall back to without if needed
      const loadImage = (withCors) => new Promise((resolve, reject) => {
        const testImg = new Image();
        if (withCors) testImg.crossOrigin = 'anonymous';
        testImg.onload = () => resolve(testImg);
        testImg.onerror = () => reject(new Error('Image load failed'));
        testImg.src = img.src;
      });

      let loadedImg;
      try {
        // Try with CORS first (needed to draw to canvas)
        loadedImg = await loadImage(true);
      } catch {
        // If CORS fails, skip compression for this image
        console.warn('Image CORS load failed, keeping original:', img.src?.substring(0, 50));
        continue;
      }

      // Calculate new dimensions maintaining aspect ratio
      let width = loadedImg.naturalWidth;
      let height = loadedImg.naturalHeight;

      // Skip if image dimensions are invalid
      if (!width || !height) continue;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(loadedImg, 0, 0, width, height);

      // Convert to compressed JPEG data URL
      const compressedSrc = canvas.toDataURL('image/jpeg', quality);
      img.src = compressedSrc;
    } catch (e) {
      // If compression fails, keep original (CORS issues, etc.)
      console.warn('Image compression failed for:', img.src?.substring(0, 50), e?.message || e);
    }
  }
}

/**
 * Print content using iframe for clean, isolated output.
 * Works across Chrome, Firefox, Safari, Edge, and mobile browsers.
 * Copies the proven pattern from ViewQuote.jsx.
 *
 * @param {Object} options
 * @param {React.RefObject} options.contentRef - Ref to the DOM element to print
 * @param {Function} [options.onComplete] - Callback when print dialog is triggered
 */
export async function printContent({ contentRef, onComplete }) {
  const content = contentRef?.current;
  if (!content) return;

  const clone = content.cloneNode(true);

  // Compress images to reduce print/PDF size
  await compressImages(clone, 800, 0.7);

  // Get styles
  const styles = Array.from(document.styleSheets)
    .map(sheet => {
      try {
        return Array.from(sheet.cssRules).map(rule => rule.cssText).join('\n');
      } catch {
        return '';
      }
    })
    .join('\n');

  // Safari needs different print CSS due to its print preview behavior
  const safariMode = isSafari();

  const printHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        ${styles}
        @page { size: A4; margin: 10mm; }
        html, body { margin: 0; padding: 0; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; background: white; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

        /* Ensure images print correctly */
        img { max-width: 100%; }

        /* Page break handling */
        .page-break-before { page-break-before: always; }
        .page-break-after { page-break-after: always; }
        .avoid-break { page-break-inside: avoid; }

        ${safariMode ? `
        /* Safari print preview has a known quirk where it clips the top ~40px in the preview.
           However, the actual PDF/print output is correct. This is a Safari preview-only issue. */
        html, body {
          -webkit-print-color-adjust: exact !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        ` : ''}
      </style>
    </head>
    <body>
      ${clone.outerHTML}
    </body>
    </html>
  `;

  // Create iframe for isolated printing
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  document.body.appendChild(iframe);

  // Track if print was already triggered to prevent double dialogs
  let printTriggered = false;
  let fallbackTimeout = null;

  const triggerPrint = () => {
    // Prevent double print dialogs
    if (printTriggered) return;
    printTriggered = true;

    // Clear any pending fallback
    if (fallbackTimeout) {
      clearTimeout(fallbackTimeout);
      fallbackTimeout = null;
    }

    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      console.error('Print error:', e);
    }

    // Notify completion
    if (onComplete) {
      onComplete();
    }

    // Remove iframe after print dialog closes
    setTimeout(() => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
    }, 1000);
  };

  if (isSafari()) {
    // Safari: use srcdoc and longer delay for rendering
    iframe.srcdoc = printHtml;
    iframe.onload = () => {
      // Safari needs extra time to render and load fonts
      setTimeout(triggerPrint, 800);
    };
    // Fallback if onload doesn't fire (rare edge case)
    fallbackTimeout = setTimeout(() => {
      if (iframe.parentNode && !printTriggered) {
        triggerPrint();
      }
    }, 3000);
  } else {
    // Chrome, Firefox, Edge, Android: use document.write (faster and more reliable)
    iframe.onload = () => {
      iframe.contentDocument.open();
      iframe.contentDocument.write(printHtml);
      iframe.contentDocument.close();
      // Wait for fonts to load
      if (iframe.contentDocument.fonts?.ready) {
        iframe.contentDocument.fonts.ready.then(() => {
          setTimeout(triggerPrint, 100);
        });
      } else {
        setTimeout(triggerPrint, 300);
      }
    };
    // Trigger the initial load
    iframe.src = 'about:blank';
  }
}

/**
 * Downloads content as a PDF using server-side rendering.
 *
 * @param {Object} options
 * @param {React.RefObject} options.contentRef - Ref to the DOM element to convert to PDF
 * @param {string} options.filename - Filename for the downloaded PDF (without .pdf extension)
 * @param {Function} [options.onSuccess] - Callback on successful download
 * @param {Function} [options.onError] - Callback on error (receives error message)
 * @param {Function} [options.onDevFallback] - Callback when running in dev environment (API not available)
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export async function downloadPdf({ contentRef, filename, onSuccess, onError, onDevFallback }) {
  try {
    // Validate content ref
    const content = contentRef?.current;
    if (!content) {
      throw new Error('Content not found');
    }

    // Clone the content
    const clone = content.cloneNode(true);

    // NOTE: We do NOT compress images here because:
    // 1. It embeds large base64 data URLs into the HTML payload
    // 2. This can exceed Vercel's 4.5MB body size limit
    // 3. Puppeteer can load images directly from URLs more efficiently
    // Image compression is handled server-side via deviceScaleFactor

    // Get all stylesheets and inline them
    const styles = Array.from(document.styleSheets)
      .map(sheet => {
        try {
          return Array.from(sheet.cssRules)
            .map(rule => rule.cssText)
            .join('\n');
        } catch {
          // CORS or other access errors for external stylesheets
          return '';
        }
      })
      .join('\n');

    // Build complete HTML document with fonts
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          ${styles}
          @page { size: A4; margin: 0; }
          body {
            margin: 0;
            padding: 0;
            font-family: 'Inter', sans-serif;
            background: white;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-family: inherit;
          }
          /* Ensure images are rendered with CORS */
          img {
            max-width: 100%;
          }
          /* Page break handling for multi-page documents */
          .page-break-before { page-break-before: always; }
          .page-break-after { page-break-after: always; }
          .avoid-break { page-break-inside: avoid; }
        </style>
      </head>
      <body>
        ${clone.outerHTML}
      </body>
      </html>
    `;

    // Send to API for PDF generation (reuse the quote PDF endpoint)
    const response = await fetch('/api/generate-quote-pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        html,
        filename
      })
    });

    if (!response.ok) {
      // Check if running on dev server (API not available)
      if (response.status === 404) {
        if (onDevFallback) {
          onDevFallback();
        }
        return false;
      }
      // Try to get error message from response
      const errorText = await response.text();
      console.error('PDF generation failed:', response.status, errorText);
      throw new Error('PDF generation failed');
    }

    // Verify we got a PDF response
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/pdf')) {
      console.error('Unexpected content type:', contentType);
      const text = await response.text();
      console.error('Response body:', text);
      throw new Error('Server did not return a PDF');
    }

    // Download the PDF
    const blob = await response.blob();

    // Verify blob has content
    if (blob.size === 0) {
      throw new Error('Received empty PDF');
    }

    // Trigger browser download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    if (onSuccess) {
      onSuccess();
    }
    return true;

  } catch (err) {
    console.error('Error downloading PDF:', err);
    if (onError) {
      onError(err.message || 'Failed to download PDF');
    }
    return false;
  }
}
